import {
  Archive,
  ClipboardList,
  Contact,
  FileSpreadsheet,
  FolderKanban,
  ClipboardCheck,
  ListChecks,
  Monitor,
  RefreshCw,
  User,
  UserPlus,
  Users,
  Building2,
} from 'lucide-react'
import { AppPaths } from '@/config/app-paths'
import type { StaffNavContext } from '../staff-nav-context'
import { group, leaf, type StaffNavGroup, type StaffNavNode } from '../nav-node'

export function buildHrGroup(ctx: StaffNavContext): StaffNavGroup | null {
  const children: StaffNavNode[] = []

  if (ctx.showWalkInNav && !ctx.walkInListedUnderRecruitment) {
    children.push(
      leaf(
        'walk_in_hr',
        'Reception Walk-in',
        AppPaths.hrWalkInIntake,
        UserPlus,
        ['walk in', 'reception', 'intake'],
      ),
    )
  }
  if (ctx.showVisaOnboardingNav) {
    children.push(
      leaf(
        'visa_onboarding',
        'Visa & Onboarding',
        AppPaths.hrOnboardingHub,
        FolderKanban,
        ['visa', 'onboarding', 'cases'],
        {
          activeWhenPrefix: true,
          // Walk-in + follow-up live under /hr/onboarding/* — do not light both.
          activeExcludePrefixes: [
            AppPaths.hrWalkInIntake,
            AppPaths.hrOnboardingFollowUp,
            AppPaths.hrOnboardingRenewals,
          ],
        },
      ),
      leaf(
        'visa_renewals',
        'Visa renewals',
        AppPaths.hrOnboardingRenewals,
        RefreshCw,
        ['visa', 'renewal', 'renew'],
        { activeWhenPrefix: true },
      ),
    )
  }
  if (ctx.showOnboardingFollowUpNav) {
    children.push(
      leaf(
        'onboarding_follow_up',
        'Case follow-up',
        AppPaths.hrOnboardingFollowUp,
        ListChecks,
        ['onboarding', 'pro', 'follow-up', 'cases', 'pipeline'],
      ),
    )
  }
  if (ctx.showInterviewIntakeNav) {
    children.push(
      leaf(
        'interview_applications',
        'Interview Applications',
        AppPaths.hrInterviewApplications,
        ClipboardCheck,
        ['interview', 'applications', 'shortlisted', 'walk-in', 'intake'],
        { activeWhenPrefix: true },
      ),
    )
  }
  if (ctx.showWorkforceDirectory) {
    children.push(
      leaf(
        'employee_directory',
        'Employee Directory',
        AppPaths.workforceDirectory,
        Contact,
        ['employee', 'directory', 'roster'],
        {
          activeWhenPrefix: true,
          activeExcludePrefixes: [AppPaths.workforceCompleteness],
        },
      ),
    )
  }
  if (ctx.showEmployeeCompleteness) {
    children.push(
      leaf(
        'employee_completeness',
        'Employee Completeness',
        AppPaths.workforceCompleteness,
        FileSpreadsheet,
        ['completeness', 'missing', 'gaps', 'backfill', 'import'],
      ),
    )
  }
  if (ctx.showStaffDeployment) {
    children.push(
      leaf(
        'staff_deployment',
        'Staff deployment',
        AppPaths.workforceDeployment,
        Users,
        ['deploy', 'deployment', 'placement', 'client'],
        { activeWhenPrefix: true },
      ),
    )
  }
  if (ctx.showDailyAttendance) {
    children.push(
      leaf(
        'daily_attendance',
        'Daily attendance',
        AppPaths.payrollDailyAttendance,
        ClipboardCheck,
        ['attendance', 'consolidate', 'report'],
        { activeWhenPrefix: true },
      ),
    )
  }
  if (ctx.showStaffRoster) {
    children.push(
      leaf(
        'staff_roster',
        'Staff roster',
        AppPaths.payrollAdminStaff,
        ClipboardList,
        ['staff', 'roster', 'card'],
        { activeWhenPrefix: true },
      ),
    )
  }
  if (ctx.showClientDirectory) {
    children.push(
      leaf(
        'client_directory',
        'Clients',
        AppPaths.adminClients,
        Building2,
        ['clients', 'client board'],
        { activeWhenPrefix: true },
      ),
    )
  }
  if (ctx.showStaffPersonalDetailsNav) {
    children.push(
      leaf(
        'staff_personal_details',
        'Staff Personal Details',
        AppPaths.hrStaffPersonalDetails,
        ClipboardList,
        ['staff', 'personal', 'details', 'intake', 'form'],
        { activeWhenPrefix: true },
      ),
    )
  }
  if (ctx.showStaffPersonalDetailsImportNav) {
    children.push(
      leaf(
        'staff_personal_details_import',
        'Import Staff Sheet',
        AppPaths.hrStaffPersonalDetailsImport,
        FileSpreadsheet,
        ['import', 'bulk', 'excel', 'sheet', 'upload', 'supervisor'],
      ),
    )
  }
  if (ctx.showDesktopActivityNav) {
    children.push(
      leaf(
        'desktop_activity',
        'Desktop Activity',
        AppPaths.adminDesktopActivity,
        Monitor,
        ['desktop', 'activity', 'screenshots', 'enrollment'],
      ),
    )
  }
  if (ctx.showPurgedEmployees) {
    children.push(
      leaf(
        'purged_employees',
        'Purged Employees',
        AppPaths.workforcePurgedEmployees,
        Archive,
        ['purge', 'archive', 'deleted employees'],
      ),
    )
  }

  return group('hr', 'HR', User, ['hr', 'onboarding', 'workforce', 'employees'], children)
}
